import React from 'react';
import { TEXT_COLOR } from '../../theme';
import { Box, Button, MenuItem, Select, Typography, OutlinedInput, FormControl } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getDynamicLabel } from '../../utils/dynamicFields';
import DynamicFieldInputV2 from './DynamicFieldInputV2';
import LanguageSwitcher from '../Topbar/LanguageSwitcher';
import {
    PAGE_BG, CARD_BG, BORDER, TEXT_MAIN, TEXT_MUTED,
    PRIMARY, PRIMARY_HOVER, CARD_RADIUS, FONT, INPUT_RADIUS,
    ERROR_COLOR, BORDER_HOVER, FS_TITLE, FS_BODY, FS_LABEL,
} from './tokens';

function FieldLabel({ children, required }) {
    return (
        <Typography
            component="label"
            sx={{
                display: 'block',
                fontWeight: 600,
                fontSize: FS_LABEL,
                color: TEXT_MUTED,
                mb: 0.5,
                letterSpacing: '0.01em',
            }}
        >
            {children}
            {required && <span style={{ color: TEXT_COLOR, marginLeft: 2 }}>*</span>}
        </Typography>
    );
}

function FieldError({ msg }) {
    if (!msg || msg.trim() === '') return null;
    return (
        <Typography sx={{ fontSize: '0.75rem', color: ERROR_COLOR, mt: 0.5 }}>
            {msg}
        </Typography>
    );
}

const fieldSx = {
    borderRadius: INPUT_RADIUS,
    fontSize: FS_BODY,
    backgroundColor: CARD_BG,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BORDER_HOVER },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY, borderWidth: 1.5 },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: ERROR_COLOR },
};

const inputSx = fieldSx;
const selectSx = fieldSx;

function makeOptions(arr, getMLText) {
    return arr
        .map(x => ({ value: x.id, key: x.id, text: getMLText(x) }))
        .sort((a, b) => a.text.localeCompare(b.text));
}

export default function QuizPersonalData({
    errors,
    dynamic_errors,
    nombre, apellido, dob, user_generic_id,
    posicion, department, business_unit, internal_group,
    positions, departments, business_units, internal_groups,
    dynamic_fields, dynamic_answers,
    id_label,
    showLanguage,
    onChange, onDateChange, onDynamicChange,
    onNext,
    getMLText,
    language,
    t,
}) {
    const posOpts = makeOptions(positions, getMLText);
    const deptOpts = makeOptions(departments, getMLText);
    const buOpts = makeOptions(business_units, getMLText);
    const igOpts = makeOptions(internal_groups, getMLText);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: PAGE_BG, fontFamily: FONT }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', p: { xs: 2, sm: 3 } }}>
                <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', my: 'auto' }}>
                    {showLanguage && (
                        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: FS_LABEL, color: TEXT_MUTED }}>
                                {t('select_language')}
                            </Typography>
                            <LanguageSwitcher />
                        </Box>
                    )}

                    <Box
                        sx={{
                            backgroundColor: CARD_BG,
                            borderRadius: CARD_RADIUS,
                            border: `1px solid ${BORDER}`,
                            p: { xs: 2.5, sm: 3.5 },
                        }}
                    >
                        <Box sx={{ mb: 3, pb: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                            <Typography sx={{ fontWeight: 700, fontSize: FS_TITLE, color: TEXT_MAIN, mb: 0.5 }}>
                                {t('quiz_tell_us')}
                            </Typography>
                            <Typography sx={{ fontSize: FS_LABEL, color: TEXT_MUTED }}>
                                {t('quiz_anon_note')}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                            <Box>
                                <FieldLabel required>{t('name')}</FieldLabel>
                                <OutlinedInput
                                    name="nombre"
                                    value={nombre}
                                    onChange={onChange}
                                    error={Boolean(errors.nombre)}
                                    fullWidth
                                    size="small"
                                    sx={inputSx}
                                />
                                <FieldError msg={errors.nombre} />
                            </Box>

                            <Box>
                                <FieldLabel required>{t('surname')}</FieldLabel>
                                <OutlinedInput
                                    name="apellido"
                                    value={apellido}
                                    onChange={onChange}
                                    error={Boolean(errors.apellido)}
                                    fullWidth
                                    size="small"
                                    sx={inputSx}
                                />
                                <FieldError msg={errors.apellido} />
                            </Box>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box>
                                    <FieldLabel required>{t('dob')}</FieldLabel>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        value={dob}
                                        onChange={onDateChange}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'DD/MM/YYYY',
                                                error: Boolean(errors.dob),
                                                sx: {
                                                    '& .MuiOutlinedInput-root': inputSx,
                                                },
                                            },
                                        }}
                                    />
                                    <FieldError msg={errors.dob} />
                                </Box>
                            </LocalizationProvider>

                            {id_label && (
                                <Box>
                                    <FieldLabel required>{id_label}</FieldLabel>
                                    <OutlinedInput
                                        name="user_generic_id"
                                        value={user_generic_id}
                                        onChange={onChange}
                                        error={Boolean(errors.user_generic_id)}
                                        fullWidth
                                        size="small"
                                        sx={inputSx}
                                    />
                                    <FieldError msg={errors.user_generic_id} />
                                </Box>
                            )}

                            {buOpts.length > 0 && (
                                <Box>
                                    <FieldLabel required>{t('business_unit')}</FieldLabel>
                                    <FormControl fullWidth size="small" error={Boolean(errors.business_unit)}>
                                        <Select
                                            value={business_unit}
                                            onChange={onChange}
                                            input={<OutlinedInput name="business_unit" sx={selectSx} />}
                                            displayEmpty
                                        >
                                            <MenuItem value={0} disabled><em>—</em></MenuItem>
                                            {buOpts.map(o => <MenuItem value={o.value} key={o.key}>{o.text}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FieldError msg={errors.business_unit} />
                                </Box>
                            )}

                            {posOpts.length > 0 && (
                                <Box>
                                    <FieldLabel required>{t('position')}</FieldLabel>
                                    <FormControl fullWidth size="small" error={Boolean(errors.posicion)}>
                                        <Select
                                            value={posicion}
                                            onChange={onChange}
                                            input={<OutlinedInput name="posicion" sx={selectSx} />}
                                            displayEmpty
                                        >
                                            <MenuItem value={0} disabled><em>—</em></MenuItem>
                                            {posOpts.map(o => <MenuItem value={o.value} key={o.key}>{o.text}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FieldError msg={errors.posicion} />
                                </Box>
                            )}

                            {deptOpts.length > 0 && (
                                <Box>
                                    <FieldLabel required>{t('area')}</FieldLabel>
                                    <FormControl fullWidth size="small" error={Boolean(errors.department)}>
                                        <Select
                                            value={department}
                                            onChange={onChange}
                                            input={<OutlinedInput name="department" sx={selectSx} />}
                                            displayEmpty
                                        >
                                            <MenuItem value={0} disabled><em>—</em></MenuItem>
                                            {deptOpts.map(o => <MenuItem value={o.value} key={o.key}>{o.text}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FieldError msg={errors.department} />
                                </Box>
                            )}

                            {igOpts.length > 0 && (
                                <Box>
                                    <FieldLabel required>{t('internal_group')}</FieldLabel>
                                    <FormControl fullWidth size="small" error={Boolean(errors.internal_group)}>
                                        <Select
                                            value={internal_group}
                                            onChange={onChange}
                                            input={<OutlinedInput name="internal_group" sx={selectSx} />}
                                            displayEmpty
                                        >
                                            <MenuItem value={0} disabled><em>—</em></MenuItem>
                                            {igOpts.map(o => <MenuItem value={o.value} key={o.key}>{o.text}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <FieldError msg={errors.internal_group} />
                                </Box>
                            )}

                            {dynamic_fields.map(f => (
                                <Box key={f.key}>
                                    <FieldLabel required={!!f.required}>
                                        {getDynamicLabel(f, language)}
                                    </FieldLabel>
                                    <DynamicFieldInputV2
                                        field={f}
                                        value={dynamic_answers[f.key]}
                                        error={dynamic_errors[f.key]}
                                        onChange={onDynamicChange(f.key)}
                                        language={language}
                                        inputSx={inputSx}
                                        selectSx={selectSx}
                                    />
                                    <FieldError msg={dynamic_errors[f.key]} />
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                            <Button
                                variant="contained"
                                onClick={onNext}
                                sx={{
                                    backgroundColor: PRIMARY,
                                    px: 5,
                                    py: 1.25,
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: FS_BODY,
                                    textTransform: 'none',
                                    '&:hover': { backgroundColor: PRIMARY_HOVER },
                                }}
                            >
                                {t('quiz_next')}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
